// Prev/next control for a horizontal scroll strip. Buttons target this element with
// commandfor + command="--prev" / "--next"; it scrolls its first [data-strip] child by most of a view
// and disables each button at its end of the strip. Native swipe/trackpad scroll keeps working.
class ScrollStrip extends HTMLElement {
  #abort = null;

  connectedCallback() {
    const strip = this.querySelector('[data-strip]');
    if (!strip || !this.id) return;
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('command', (event) => {
      if (event.command !== '--prev' && event.command !== '--next') return;
      const direction = event.command === '--next' ? 1 : -1;
      const rtl = getComputedStyle(strip).direction === 'rtl' ? -1 : 1;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      strip.scrollBy({ left: direction * rtl * strip.clientWidth * 0.8, behavior: reduceMotion ? 'auto' : 'smooth' });
    }, { signal });

    // Arrow state is checked at most once per frame; touch phones hide the arrows, so they skip it.
    const arrowsShown = window.matchMedia('(hover: hover)');
    let queued = false;
    const schedule = () => {
      if (queued || !arrowsShown.matches) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        this.#update(strip);
      });
    };
    strip.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', schedule, { passive: true, signal });
    this.#update(strip);
    this.#watchFocus(strip, signal);
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  // Touch screens have no hover, so with data-focus-color the tiles crossing the middle of the
  // strip get data-in-focus, letting CSS bring them out of grayscale as the visitor swipes.
  #watchFocus(strip, signal) {
    if (!this.hasAttribute('data-focus-color') || !window.matchMedia('(hover: none)').matches) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => entry.target.toggleAttribute('data-in-focus', entry.isIntersecting));
    }, { root: strip, rootMargin: '0px -35% 0px -35%' });
    strip.querySelectorAll(':scope > li').forEach((tile) => observer.observe(tile));
    signal.addEventListener('abort', () => observer.disconnect());
  }

  #update(strip) {
    const max = strip.scrollWidth - strip.clientWidth;
    const position = Math.abs(strip.scrollLeft);
    document.querySelectorAll(`[commandfor="${this.id}"]`).forEach((button) => {
      const atEnd = button.getAttribute('command') === '--prev' ? position <= 2 : position >= max - 2;
      button.disabled = atEnd;
    });
  }
}

if (!customElements.get('s-scroll-strip')) customElements.define('s-scroll-strip', ScrollStrip);
